import * as _ from 'lodash';

import {
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  BankAccountWithInstitutionAndDesignationGQLType,
  FundingRequestAllocationsGQLType,
  FundingRequestComponentGQLType,
  FundingRequestComponentTypeEnumGQLType,
  FundingRequestConstraintGQLType,
  FundingRequestEventForRenderFragmentGQLType,
  FundingRequestReportGQLType,
  FundingRequestStatusEnumGQLType,
  FundingRequestWithFullDetailsFragmentGQLType,
  GetFundingRequestWithFullDetailsAndConstraintQueryGQLType,
  Maybe,
  UpdateFundingRequestComponentChangesetGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';
import {
  BankAccountMap,
  FundingRequestComponentContainer,
  FundingRequestComponentDefaultTargetFundingDates,
  FundingRequestComponentTotalAmounts,
} from 'types/fundingRequests';

type ConstraintItem = {
  max: number;
  min: number;
  step: number;
};

type MasterConstraints = {
  collectionsRecycleContributesToBorrowingBase: boolean;
  collectionsRecycleMax: number;
  collectionsPaydownMax: number;
  drawRequestContributesToBorrowingBase: boolean;
  drawRequestIncrementAmount: Maybe<number>;
  isStopDrawEnforced: boolean;
  isCollectionsRecycleEnabled: boolean;
  isVoluntaryPaydownAllowed: boolean;
  maxCollectionsRecycleAmount: number;
  preAdvanceCashContributionToBorrowingBase: number;
  maxFundingRequestAmount: number;
  maxDrawRequestAmount: number;
  minimumDrawRequestAmount: number;
  minimumRepaymentAmount: number;
};

type FundingRequestConstraintFE = {
  curePaydown: ConstraintItem;
  collectionsPaydown: ConstraintItem;
  collectionsRecycle: ConstraintItem;
  drawRequest: ConstraintItem;
  _masterConstraints: MasterConstraints; // READONLY after initial set
};

// TODO: add '_' prefix to denote READYONLY props ?
export type FundingRequestState = {
  creditAgreementId: string;
  creditFacilityId: string;
  creditFacilityName: string;
  bankAccountMap: BankAccountMap;
  borrowerKey: string;
  _status: FundingRequestStatusEnumGQLType | '';
  components: FundingRequestComponentContainer;
  componentDefaultTargetFundingDates: FundingRequestComponentDefaultTargetFundingDates;
  componentTotalAmounts: FundingRequestComponentTotalAmounts;
  constraints: FundingRequestConstraintFE;
  borrowingBaseUtilizationPercentage: number;
  fundingRequestEvents: FundingRequestEventForRenderFragmentGQLType[];
  fundingRequestReports: FundingRequestReportGQLType[];
  _reportingDate: Maybe<Util.FullDateStr>;
  _fileVersion: Maybe<number>;
  _commitmentAmount: number;
  _borrowingBaseUtilizationPercentage: number;
  _adjustedPrincipalBalance: number;
  _currentPrincipalBalance: number;
  _totalCash: number;
  _borrowingBaseAmountAssets: number;
  _totalBorrowingBaseAmount: number;
  _cashAdvanceRate: number;
  _maxReceivableAdvanceAmount: number;
  _totalMaxAdvanceAmount: number;
  _cashReserveAmount: number;
  _borrowingBaseReserveAmount: number;
};

const CURE_PAYDOWN_KEY = 'curePaydown' as const;
const COLLECTIONS_PAYDOWN_KEY = 'collectionsPaydown' as const;
const COLLECTIONS_RECYCLE_KEY = 'collectionsRecycle' as const;
const DRAW_REQUEST_KEY = 'drawRequest' as const;

const UPDATE_CONTROL_METRICS = 'UPDATE_CONTROL_METRICS';
const SET_FUNDING_REQUEST_WITH_FULL_DETAILS_AND_CONSTRAINT =
  'SET_FUNDING_REQUEST_WITH_FULL_DETAILS_AND_CONSTRAINT';
const REFRESH_FUNDING_REQUEST_WITH_FULL_DETAILS =
  'REFRESH_FUNDING_REQUEST_WITH_FULL_DETAILS';
const ALLOCATE_FUNDING_REQUEST_TOTAL_AMOUNTS =
  'ALLOCATE_FUNDING_REQUEST_TOTAL_AMOUNTS';
const UPDATE_TARGET_FUNDING_DATE = 'UPDATE_TARGET_FUNDING_DATE';

const dummyComponentFactory = (
  type: FundingRequestComponentTypeEnumGQLType,
): UpdateFundingRequestComponentChangesetGQLType[] => {
  return [
    {
      id: 'dummy',
      type,
      amount: 0,
      sourceBankAccountId: 'dummy',
      destinationBankAccountId: 'dummy',
      targetFundingDate: '2000-01-01',
    },
  ];
};

const constraintsDefaultFactory = () => ({
  max: 100,
  min: 0,
  step: 1,
});

const initialConstraints: FundingRequestConstraintFE = {
  curePaydown: constraintsDefaultFactory(),
  collectionsPaydown: constraintsDefaultFactory(),
  collectionsRecycle: constraintsDefaultFactory(),
  drawRequest: constraintsDefaultFactory(),
  // effectiveAdvanceRatePercentage: constraintsDefaultFactory(),
  _masterConstraints: {
    collectionsRecycleMax: Infinity,
    collectionsPaydownMax: Infinity,
    drawRequestIncrementAmount: null,
    maxFundingRequestAmount: Infinity,
    maxDrawRequestAmount: Infinity,
    maxCollectionsRecycleAmount: Infinity,
    preAdvanceCashContributionToBorrowingBase: Infinity,
    isStopDrawEnforced: false,
    isCollectionsRecycleEnabled: true,
    isVoluntaryPaydownAllowed: false,
    collectionsRecycleContributesToBorrowingBase: false,
    drawRequestContributesToBorrowingBase: false,
    minimumDrawRequestAmount: 0,
    minimumRepaymentAmount: 0,
  },
};

export const fundingRequestInitialState = ({
  borrowerKey,
}: {
  borrowerKey: string;
}): FundingRequestState => ({
  bankAccountMap: new Map<
    string,
    BankAccountWithInstitutionAndDesignationGQLType
  >(),
  borrowerKey,
  creditAgreementId: '',
  creditFacilityId: '',
  creditFacilityName: '',
  _status: '',
  components: {
    curePaydown: dummyComponentFactory('curePaydown'),
    collectionsPaydown: dummyComponentFactory('collectionsPaydown'),
    collectionsRecycle: dummyComponentFactory('collectionsRecycle'),
    drawRequest: dummyComponentFactory('drawRequest'),
  },
  componentTotalAmounts: {
    curePaydownTotalAmount: 0,
    collectionsPaydownTotalAmount: 0,
    collectionsRecycleTotalAmount: 0,
    drawRequestTotalAmount: 0,
  },
  componentDefaultTargetFundingDates: {
    curePaydown:
      dummyComponentFactory('curePaydown')[0].targetFundingDate ?? undefined,
    collectionsPaydown:
      dummyComponentFactory('collectionsPaydown')[0].targetFundingDate ??
      undefined,
    collectionsRecycle:
      dummyComponentFactory('collectionsRecycle')[0].targetFundingDate ??
      undefined,
    drawRequest:
      dummyComponentFactory('drawRequest')[0].targetFundingDate ?? undefined,
  },
  // TODO: These metrics are FE only for computing purpose?
  constraints: initialConstraints,
  borrowingBaseUtilizationPercentage: 0.8,
  fundingRequestEvents: [],
  fundingRequestReports: [],
  _commitmentAmount: Infinity,
  _borrowingBaseUtilizationPercentage: 0.8,
  _adjustedPrincipalBalance: 0,
  _currentPrincipalBalance: 0,
  _totalCash: 0,
  _borrowingBaseAmountAssets: 0,
  _totalBorrowingBaseAmount: 0,
  _cashAdvanceRate: 0,
  _reportingDate: null,
  _fileVersion: null,
  _maxReceivableAdvanceAmount: 0,
  _totalMaxAdvanceAmount: 0,
  _cashReserveAmount: 0,
  _borrowingBaseReserveAmount: 0,
});

const _calculateBorrowingBaseUtilizationPercentage = ({
  _currentPrincipalBalance,
  _totalBorrowingBaseAmount,
  componentTotalAmounts,
  collectionsRecycleContributesToBorrowingBase,
  drawRequestContributesToBorrowingBase,
}: {
  _currentPrincipalBalance: number;
  _totalBorrowingBaseAmount: number;
  componentTotalAmounts: FundingRequestComponentTotalAmounts;
  collectionsRecycleContributesToBorrowingBase: boolean;
  drawRequestContributesToBorrowingBase: boolean;
}): number => {
  const totalCollectionsRecycleAmount = collectionsRecycleContributesToBorrowingBase
    ? componentTotalAmounts.collectionsPaydownTotalAmount
    : componentTotalAmounts.collectionsPaydownTotalAmount +
      componentTotalAmounts.collectionsRecycleTotalAmount;

  return drawRequestContributesToBorrowingBase
    ? ((_currentPrincipalBalance +
        componentTotalAmounts.drawRequestTotalAmount -
        componentTotalAmounts.collectionsPaydownTotalAmount -
        componentTotalAmounts.curePaydownTotalAmount) /
        (_totalBorrowingBaseAmount -
          totalCollectionsRecycleAmount +
          componentTotalAmounts.drawRequestTotalAmount)) *
        100
    : ((_currentPrincipalBalance +
        componentTotalAmounts.drawRequestTotalAmount -
        componentTotalAmounts.collectionsPaydownTotalAmount -
        componentTotalAmounts.curePaydownTotalAmount) /
        (_totalBorrowingBaseAmount - totalCollectionsRecycleAmount)) *
        100;
};

const _calculateCollectionsRecycleMax = ({
  maxCollectionsRecycleAmount,
  collectionsRecycleMax,
  _adjustedPrincipalBalance,
  _maxReceivableAdvanceAmount,
  totalCollectionsPaydownAmount,
  isCollectionsRecycleEnabled,
  collectionsRecycleContributesToBorrowingBase,
  _cashAdvanceRate,
  preAdvanceCashContributionToBorrowingBase,
}: {
  maxCollectionsRecycleAmount: number;
  collectionsRecycleMax: number;
  _adjustedPrincipalBalance: number;
  _maxReceivableAdvanceAmount: number;
  totalCollectionsPaydownAmount: number;
  isCollectionsRecycleEnabled: boolean;
  collectionsRecycleContributesToBorrowingBase: boolean;
  _cashAdvanceRate: number;
  preAdvanceCashContributionToBorrowingBase: number;
}): number => {
  // if we are propping up the borrowing base by cash, calculate the amount of cash we need to leave behind based on funding deficit
  const fundingDeficit =
    _adjustedPrincipalBalance - _maxReceivableAdvanceAmount; // guaranteed to be positive when cash supports borrowing base
  const cashDeficit = commonUtils.divideByOrZero(
    fundingDeficit,
    _cashAdvanceRate, // convert from funding basis back to cash basis
  );
  return Math.max(
    Math.min(
      isCollectionsRecycleEnabled === true
        ? Math.max(maxCollectionsRecycleAmount, 0) -
            totalCollectionsPaydownAmount
        : 0,
      collectionsRecycleContributesToBorrowingBase === false
        ? // This sets max when cash is suppporting the borrowing base
          preAdvanceCashContributionToBorrowingBase - Math.max(cashDeficit, 0)
        : Infinity,
      collectionsRecycleMax,
    ),
    0,
  );
};

const _calculateCollectionsPaydownMax = ({
  maxCollectionsRecycleAmount,
  collectionsPaydownMax,
  _currentPrincipalBalance,
  totalCollectionsRecycleAmount,
  isVoluntaryPaydownAllowed,
}: {
  maxCollectionsRecycleAmount: number;
  collectionsPaydownMax: number;
  _currentPrincipalBalance: number;
  totalCollectionsRecycleAmount: number;
  isVoluntaryPaydownAllowed: boolean;
}): number => {
  return Math.max(
    Math.min(
      isVoluntaryPaydownAllowed === false
        ? 0
        : maxCollectionsRecycleAmount - totalCollectionsRecycleAmount,
      _currentPrincipalBalance,
      collectionsPaydownMax,
    ),
    0,
  );
};

const _calculateDrawRequestMax = ({
  collectionsRecycleContributesToBorrowingBase,
  maxFundingRequestAmount,
  maxDrawRequestAmount,
  totalCollectionsPaydownAmount,
  totalCollectionsRecycleAmount,
  _commitmentAmount,
  _cashAdvanceRate,
  drawRequestIncrementAmount,
  _adjustedPrincipalBalance,
  isStopDrawEnforced,
  maxCollectionsRecycleAmount,
  preAdvanceCashContributionToBorrowingBase,
}: {
  collectionsRecycleContributesToBorrowingBase: boolean;
  maxFundingRequestAmount: number;
  maxDrawRequestAmount: number;
  totalCollectionsPaydownAmount: number;
  totalCollectionsRecycleAmount: number;
  _commitmentAmount: number;
  _cashAdvanceRate: number;
  drawRequestIncrementAmount: Maybe<number>;
  _adjustedPrincipalBalance: number;
  isStopDrawEnforced: boolean;
  maxCollectionsRecycleAmount: number;
  preAdvanceCashContributionToBorrowingBase: number;
}): number => {
  if (isStopDrawEnforced === true && maxCollectionsRecycleAmount < 0) return 0;

  let drawRequestMax =
    collectionsRecycleContributesToBorrowingBase === true
      ? maxFundingRequestAmount -
        Math.min(
          totalCollectionsPaydownAmount,
          Math.max(preAdvanceCashContributionToBorrowingBase, 0),
        ) *
          _cashAdvanceRate
      : maxFundingRequestAmount -
        _cashAdvanceRate *
          Math.min(
            totalCollectionsPaydownAmount + totalCollectionsRecycleAmount,
            Math.max(preAdvanceCashContributionToBorrowingBase, 0),
          );
  drawRequestMax = Math.min(
    drawRequestMax,
    _commitmentAmount - _adjustedPrincipalBalance,
    maxDrawRequestAmount,
  );
  drawRequestMax = Math.max(0, drawRequestMax);
  drawRequestMax = drawRequestIncrementAmount
    ? Math.floor(drawRequestMax / drawRequestIncrementAmount) *
      drawRequestIncrementAmount
    : drawRequestMax;

  return drawRequestMax;
};

/**
 * helper method to update state on a cure paydown change
 */
const _updateCurePaydownChange = (
  state: FundingRequestState,
  action: { type: string; payload: any },
) => {
  if (!_.isFinite(_.toNumber(action.payload.amount))) {
    return state;
  }

  const _state = _.cloneDeep(state);
  const _action = _.cloneDeep(action);
  const { payload: _payload } = _action;
  const {
    constraints: {
      curePaydown: curePaydownConstraint,
      _masterConstraints: {
        collectionsRecycleContributesToBorrowingBase,
        drawRequestContributesToBorrowingBase,
      },
    },
    _currentPrincipalBalance,
    _totalBorrowingBaseAmount,
    componentTotalAmounts,
  } = _state;

  // update cure paydown
  _payload.amount = Math.min(_payload.amount, curePaydownConstraint.max);
  _payload.amount = Math.max(_payload.amount, curePaydownConstraint.min);
  componentTotalAmounts.curePaydownTotalAmount = Math.max(
    _payload.amount,
    curePaydownConstraint.min,
  );

  // update effective advance rate percentage
  _state.borrowingBaseUtilizationPercentage = _calculateBorrowingBaseUtilizationPercentage(
    {
      _currentPrincipalBalance,
      _totalBorrowingBaseAmount,
      componentTotalAmounts,
      collectionsRecycleContributesToBorrowingBase,
      drawRequestContributesToBorrowingBase,
    },
  );

  return {
    ..._state,
  };
};

/**
 * helper method to update state on a collections recycle change
 */
const _updateCollectionsRecycleChange = (
  state: FundingRequestState,
  action: { type: string; payload: any },
) => {
  if (!_.isFinite(_.toNumber(action.payload.amount))) {
    return state;
  }

  const _state = _.cloneDeep(state);
  const _action = _.cloneDeep(action);
  const { payload: _payload } = _action;
  const {
    constraints: {
      collectionsPaydown: collectionsPaydownConstraint,
      collectionsRecycle: collectionsRecycleConstraint,
      drawRequest: drawRequestConstraint,
      _masterConstraints: {
        isVoluntaryPaydownAllowed,
        isStopDrawEnforced,
        collectionsRecycleContributesToBorrowingBase,
        collectionsPaydownMax,
        drawRequestIncrementAmount,
        maxCollectionsRecycleAmount,
        maxFundingRequestAmount,
        maxDrawRequestAmount,
        minimumDrawRequestAmount,
        minimumRepaymentAmount,
        drawRequestContributesToBorrowingBase,
        preAdvanceCashContributionToBorrowingBase,
      },
    },
    _commitmentAmount,
    _adjustedPrincipalBalance,
    _currentPrincipalBalance,
    _totalBorrowingBaseAmount,
    componentTotalAmounts,
    _cashAdvanceRate,
  } = _state;

  // update collections recycle
  _payload.amount = Math.min(_payload.amount, collectionsRecycleConstraint.max);
  _payload.amount = Math.max(_payload.amount, collectionsRecycleConstraint.min);
  componentTotalAmounts.collectionsRecycleTotalAmount = Math.max(
    _payload.amount,
    collectionsRecycleConstraint.min,
  );

  // update collections paydown
  collectionsPaydownConstraint.max = _calculateCollectionsPaydownMax({
    maxCollectionsRecycleAmount,
    collectionsPaydownMax,
    _currentPrincipalBalance,
    totalCollectionsRecycleAmount:
      componentTotalAmounts.collectionsRecycleTotalAmount,
    isVoluntaryPaydownAllowed,
  });

  componentTotalAmounts.collectionsPaydownTotalAmount = Math.min(
    componentTotalAmounts.collectionsPaydownTotalAmount,
    collectionsPaydownConstraint.max,
  );
  componentTotalAmounts.collectionsPaydownTotalAmount = Math.max(
    componentTotalAmounts.collectionsPaydownTotalAmount,
    collectionsPaydownConstraint.min,
  );
  componentTotalAmounts.collectionsPaydownTotalAmount =
    componentTotalAmounts.collectionsPaydownTotalAmount < minimumRepaymentAmount
      ? 0
      : componentTotalAmounts.collectionsPaydownTotalAmount;

  // update draw request
  drawRequestConstraint.max = _calculateDrawRequestMax({
    collectionsRecycleContributesToBorrowingBase,
    maxFundingRequestAmount,
    maxDrawRequestAmount,
    totalCollectionsPaydownAmount:
      componentTotalAmounts.collectionsPaydownTotalAmount,
    totalCollectionsRecycleAmount:
      componentTotalAmounts.collectionsRecycleTotalAmount,
    _cashAdvanceRate,
    _commitmentAmount,
    _adjustedPrincipalBalance,
    drawRequestIncrementAmount,
    isStopDrawEnforced,
    maxCollectionsRecycleAmount,
    preAdvanceCashContributionToBorrowingBase,
  });

  componentTotalAmounts.drawRequestTotalAmount = Math.min(
    componentTotalAmounts.drawRequestTotalAmount,
    drawRequestConstraint.max,
  );
  componentTotalAmounts.drawRequestTotalAmount = Math.max(
    componentTotalAmounts.drawRequestTotalAmount,
    drawRequestConstraint.min,
  );
  if (drawRequestConstraint.max < minimumDrawRequestAmount) {
    componentTotalAmounts.drawRequestTotalAmount = 0;
  }
  if (componentTotalAmounts.drawRequestTotalAmount < minimumDrawRequestAmount) {
    componentTotalAmounts.drawRequestTotalAmount = 0;
  }

  _state.borrowingBaseUtilizationPercentage = _calculateBorrowingBaseUtilizationPercentage(
    {
      _currentPrincipalBalance,
      _totalBorrowingBaseAmount,
      componentTotalAmounts,
      collectionsRecycleContributesToBorrowingBase,
      drawRequestContributesToBorrowingBase,
    },
  );

  return {
    ..._state,
    constraints: {
      ...state.constraints,
      collectionsPaydown: collectionsPaydownConstraint,
      collectionsRecycle: collectionsRecycleConstraint,
      drawRequest: drawRequestConstraint,
    },
  };
};

/**
 * helper method to update state on a collections paydown change
 */
const _updateCollectionsPaydownChange = (
  state: FundingRequestState,
  action: { type: string; payload: any },
) => {
  if (!_.isFinite(_.toNumber(action.payload.amount))) {
    return state;
  }
  const _state = _.cloneDeep(state);
  const _action = _.cloneDeep(action);
  const { payload: _payload } = _action;
  const {
    constraints: {
      collectionsPaydown: collectionsPaydownConstraint,
      collectionsRecycle: collectionsRecycleConstraint,
      drawRequest: drawRequestConstraint,
      _masterConstraints: {
        isCollectionsRecycleEnabled,
        isStopDrawEnforced,
        collectionsRecycleContributesToBorrowingBase,
        collectionsRecycleMax,
        drawRequestContributesToBorrowingBase,
        drawRequestIncrementAmount,
        maxCollectionsRecycleAmount,
        maxFundingRequestAmount,
        maxDrawRequestAmount,
        minimumDrawRequestAmount,
        minimumRepaymentAmount,
        preAdvanceCashContributionToBorrowingBase,
      },
    },
    _commitmentAmount,
    _totalBorrowingBaseAmount,
    _adjustedPrincipalBalance,
    _currentPrincipalBalance,
    _cashAdvanceRate,
    componentTotalAmounts,
    _maxReceivableAdvanceAmount,
  } = _state;

  // update collections paydown
  _payload.amount = Math.min(
    _payload.amount,
    collectionsPaydownConstraint.max,
    _currentPrincipalBalance,
  );
  _payload.amount = Math.max(_payload.amount, collectionsPaydownConstraint.min);
  componentTotalAmounts.collectionsPaydownTotalAmount = Math.max(
    _payload.amount,
    collectionsPaydownConstraint.min,
  );
  componentTotalAmounts.collectionsPaydownTotalAmount =
    componentTotalAmounts.collectionsPaydownTotalAmount < minimumRepaymentAmount
      ? 0
      : componentTotalAmounts.collectionsPaydownTotalAmount;

  // update collections recycle
  collectionsRecycleConstraint.max = _calculateCollectionsRecycleMax({
    maxCollectionsRecycleAmount,
    collectionsRecycleMax,
    _adjustedPrincipalBalance,
    _maxReceivableAdvanceAmount,
    totalCollectionsPaydownAmount:
      componentTotalAmounts.collectionsPaydownTotalAmount,
    isCollectionsRecycleEnabled,
    collectionsRecycleContributesToBorrowingBase,
    _cashAdvanceRate,
    preAdvanceCashContributionToBorrowingBase,
  });
  componentTotalAmounts.collectionsRecycleTotalAmount = Math.min(
    componentTotalAmounts.collectionsRecycleTotalAmount,
    collectionsRecycleConstraint.max,
  );
  componentTotalAmounts.collectionsRecycleTotalAmount = Math.max(
    componentTotalAmounts.collectionsRecycleTotalAmount,
    collectionsRecycleConstraint.min,
  );

  // update draw request
  if (componentTotalAmounts.collectionsPaydownTotalAmount > 0) {
    componentTotalAmounts.drawRequestTotalAmount = 0;
  }
  componentTotalAmounts.drawRequestTotalAmount = 0;
  drawRequestConstraint.max = _calculateDrawRequestMax({
    collectionsRecycleContributesToBorrowingBase,
    maxFundingRequestAmount,
    maxDrawRequestAmount,
    totalCollectionsPaydownAmount:
      componentTotalAmounts.collectionsPaydownTotalAmount,
    totalCollectionsRecycleAmount:
      componentTotalAmounts.collectionsRecycleTotalAmount,
    _cashAdvanceRate,
    _commitmentAmount,
    _adjustedPrincipalBalance,
    drawRequestIncrementAmount,
    isStopDrawEnforced,
    maxCollectionsRecycleAmount,
    preAdvanceCashContributionToBorrowingBase,
  });

  componentTotalAmounts.drawRequestTotalAmount = Math.min(
    componentTotalAmounts.drawRequestTotalAmount,
    drawRequestConstraint.max,
  );
  componentTotalAmounts.drawRequestTotalAmount = Math.max(
    componentTotalAmounts.drawRequestTotalAmount,
    drawRequestConstraint.min,
  );
  if (drawRequestConstraint.max < minimumDrawRequestAmount) {
    componentTotalAmounts.drawRequestTotalAmount = 0;
  }
  if (componentTotalAmounts.drawRequestTotalAmount < minimumDrawRequestAmount) {
    componentTotalAmounts.drawRequestTotalAmount = 0;
  }

  _state.borrowingBaseUtilizationPercentage = _calculateBorrowingBaseUtilizationPercentage(
    {
      _currentPrincipalBalance,
      _totalBorrowingBaseAmount,
      componentTotalAmounts,
      collectionsRecycleContributesToBorrowingBase,
      drawRequestContributesToBorrowingBase,
    },
  );

  return {
    ..._state,
    constraints: {
      ...state.constraints,
      collectionsPaydown: collectionsPaydownConstraint,
      collectionsRecycle: collectionsRecycleConstraint,
      drawRequest: drawRequestConstraint,
    },
  };
};

/**
 * helper method to update state on a draw request change
 */
const _updateDrawRequestChange = (
  state: FundingRequestState,
  action: { type: string; payload: any },
) => {
  if (!_.isFinite(_.toNumber(action.payload.amount))) {
    return state;
  }
  const _state = _.cloneDeep(state);
  const _action = _.cloneDeep(action);
  const { payload: _payload } = _action;
  const {
    constraints: {
      drawRequest: drawRequestConstraint,
      collectionsRecycle: collectionsRecycleConstraint,
      collectionsPaydown: collectionsPaydownConstraint,
      _masterConstraints: {
        isCollectionsRecycleEnabled,
        collectionsRecycleContributesToBorrowingBase,
        collectionsPaydownMax,
        collectionsRecycleMax,
        drawRequestContributesToBorrowingBase,
        isVoluntaryPaydownAllowed,
        maxCollectionsRecycleAmount,
        maxDrawRequestAmount,
        drawRequestIncrementAmount,
        minimumDrawRequestAmount,
        preAdvanceCashContributionToBorrowingBase,
      },
    },
    _totalBorrowingBaseAmount,
    _adjustedPrincipalBalance,
    _currentPrincipalBalance,
    _maxReceivableAdvanceAmount,
    componentTotalAmounts,
    _cashAdvanceRate,
  } = _state;

  // update draw request
  _payload.amount = Math.min(
    _payload.amount,
    drawRequestConstraint.max,
    maxDrawRequestAmount,
  );
  _payload.amount = Math.max(_payload.amount, drawRequestConstraint.min);
  _payload.amount = drawRequestIncrementAmount
    ? Math.floor(_payload.amount / drawRequestIncrementAmount) *
      drawRequestIncrementAmount
    : _payload.amount;
  if (drawRequestConstraint.max < minimumDrawRequestAmount) {
    _payload.amount = 0;
  }
  if (_payload.amount < minimumDrawRequestAmount) {
    _payload.amount = 0;
  }
  componentTotalAmounts.drawRequestTotalAmount = Math.max(
    _payload.amount,
    drawRequestConstraint.min,
  );

  // update collections paydown
  if (componentTotalAmounts.drawRequestTotalAmount > 0) {
    componentTotalAmounts.collectionsPaydownTotalAmount = 0;
  }

  // update collections recycle
  collectionsRecycleConstraint.max = _calculateCollectionsRecycleMax({
    maxCollectionsRecycleAmount,
    collectionsRecycleMax,
    _adjustedPrincipalBalance,
    _maxReceivableAdvanceAmount,
    totalCollectionsPaydownAmount:
      componentTotalAmounts.collectionsPaydownTotalAmount,
    isCollectionsRecycleEnabled,
    collectionsRecycleContributesToBorrowingBase,
    _cashAdvanceRate,
    preAdvanceCashContributionToBorrowingBase,
  });

  // update collections paydown max
  collectionsPaydownConstraint.max = _calculateCollectionsPaydownMax({
    maxCollectionsRecycleAmount,
    collectionsPaydownMax,
    _currentPrincipalBalance,
    totalCollectionsRecycleAmount:
      componentTotalAmounts.collectionsRecycleTotalAmount,
    isVoluntaryPaydownAllowed,
  });

  _state.borrowingBaseUtilizationPercentage = _calculateBorrowingBaseUtilizationPercentage(
    {
      _currentPrincipalBalance,
      _totalBorrowingBaseAmount,
      componentTotalAmounts,
      collectionsRecycleContributesToBorrowingBase,
      drawRequestContributesToBorrowingBase,
    },
  );

  return {
    ..._state,
    constraints: {
      ...state.constraints,
      drawRequest: drawRequestConstraint,
      collectionsRecycle: collectionsRecycleConstraint,
      collectionsPaydown: collectionsPaydownConstraint,
    },
  };
};

/**
 * Convert server-side constraint data into FE usable constraints (initially).  FE side constraints data will change,
 * as component values get adjusted.
 */
export const _getConstraintsFromServerData = ({
  fundingRequestConstraint,
  _commitmentAmount,
  _adjustedPrincipalBalance,
  _currentPrincipalBalance,
  _cashAdvanceRate,
  _maxReceivableAdvanceAmount,
}: {
  fundingRequestConstraint: Pick<
    FundingRequestConstraintGQLType,
    | 'collectionsRecycleContributesToBorrowingBase'
    | 'collectionsRecycleMax'
    | 'collectionsPaydownMax'
    | 'drawRequestContributesToBorrowingBase'
    | 'drawRequestIncrementAmount'
    | 'isCollectionsRecycleEnabled'
    | 'isStopDrawEnforced'
    | 'isVoluntaryPaydownAllowed'
    | 'maxCollectionsRecycleAmount'
    | 'maxFundingRequestAmount'
    | 'maxDrawRequestAmount'
    | 'minimumDrawRequestAmount'
    | 'preAdvanceCashContributionToBorrowingBase'
    | 'targetDrawRequestAmount'
    | 'targetCollectionsRecycleAmount'
    | 'targetCollectionsPaydownAmount'
    | 'targetCurePaydownAmount'
    | 'minimumRepaymentAmount'
  >;
  _commitmentAmount: number;
  _adjustedPrincipalBalance: number;
  _currentPrincipalBalance: number;
  _cashAdvanceRate: number;
  _maxReceivableAdvanceAmount: number;
}) => {
  const {
    collectionsRecycleContributesToBorrowingBase,
    collectionsRecycleMax,
    collectionsPaydownMax,
    drawRequestContributesToBorrowingBase,
    drawRequestIncrementAmount,
    isVoluntaryPaydownAllowed,
    isCollectionsRecycleEnabled,
    isStopDrawEnforced,
    maxCollectionsRecycleAmount,
    maxFundingRequestAmount,
    minimumDrawRequestAmount,
    preAdvanceCashContributionToBorrowingBase,
    targetCollectionsPaydownAmount,
    targetCollectionsRecycleAmount,
    targetCurePaydownAmount,
    minimumRepaymentAmount,
  } = fundingRequestConstraint;

  const maxDrawRequestAmount = fundingRequestConstraint.maxDrawRequestAmount
    ? fundingRequestConstraint.maxDrawRequestAmount
    : Infinity;

  const collectionsRecycleConstraintMax = _calculateCollectionsRecycleMax({
    isCollectionsRecycleEnabled,
    maxCollectionsRecycleAmount,
    collectionsRecycleMax,
    _adjustedPrincipalBalance,
    _maxReceivableAdvanceAmount,
    totalCollectionsPaydownAmount: targetCollectionsPaydownAmount,
    collectionsRecycleContributesToBorrowingBase,
    _cashAdvanceRate,
    preAdvanceCashContributionToBorrowingBase,
  });

  const collectionsPaydownConstraintMax = _calculateCollectionsPaydownMax({
    maxCollectionsRecycleAmount,
    collectionsPaydownMax,
    _currentPrincipalBalance,
    totalCollectionsRecycleAmount: targetCollectionsRecycleAmount,
    isVoluntaryPaydownAllowed,
  });

  const drawRequestConstraintMax = _calculateDrawRequestMax({
    collectionsRecycleContributesToBorrowingBase,
    maxFundingRequestAmount,
    maxDrawRequestAmount,
    totalCollectionsPaydownAmount: targetCollectionsPaydownAmount,
    totalCollectionsRecycleAmount: targetCollectionsRecycleAmount,
    _cashAdvanceRate,
    _commitmentAmount,
    _adjustedPrincipalBalance,
    drawRequestIncrementAmount,
    isStopDrawEnforced,
    maxCollectionsRecycleAmount,
    preAdvanceCashContributionToBorrowingBase,
  });

  let curePaydownConstraintMax: number;
  let curePaydownConstraintMin: number;
  if (maxFundingRequestAmount >= 0) {
    curePaydownConstraintMax = 0;
    curePaydownConstraintMin = 0;
  } else {
    curePaydownConstraintMax = targetCurePaydownAmount;
    curePaydownConstraintMin = targetCurePaydownAmount;
  }

  return {
    collectionsRecycle: {
      max: collectionsRecycleConstraintMax,
      min: 0,
      step: 1,
    },
    collectionsPaydown: {
      max: collectionsPaydownConstraintMax,
      min: 0,
      step: 1,
    },
    drawRequest: {
      max: drawRequestConstraintMax,
      min: 0, // TODO: update with actual min, with on/off mode
      step: drawRequestIncrementAmount || 1,
    },
    curePaydown: {
      max: curePaydownConstraintMax,
      min: curePaydownConstraintMin,
      step: 1,
    },
    _masterConstraints: {
      collectionsRecycleContributesToBorrowingBase,
      collectionsRecycleMax,
      collectionsPaydownMax,
      drawRequestContributesToBorrowingBase,
      drawRequestIncrementAmount,
      isStopDrawEnforced,
      isCollectionsRecycleEnabled,
      isVoluntaryPaydownAllowed,
      maxCollectionsRecycleAmount,
      maxFundingRequestAmount,
      maxDrawRequestAmount,
      minimumDrawRequestAmount,
      minimumRepaymentAmount,
      preAdvanceCashContributionToBorrowingBase,
    },
  };
};

// Does a hard refresh of the FE's fundingRequestWithFullDetails
// Should only be done upon a server call that returns the entire funding request
const _refreshFundingRequestWithFullDetails = (
  currentState: FundingRequestState,
  fundingRequestWithFullDetails: FundingRequestWithFullDetailsFragmentGQLType,
): FundingRequestState => {
  const {
    adjustedPrincipalBalance: _adjustedPrincipalBalance,
    bankAccounts,
    borrowingBaseReport,
    status: _status,
    collectionsPaydownTotalAmount,
    collectionsRecycleTotalAmount,
    creditFacility,
    curePaydownTotalAmount,
    drawRequestTotalAmount,
    fundingRequestEvents,
    fundingRequestReports,
  } = fundingRequestWithFullDetails;

  finleyAssert(
    borrowingBaseReport,
    `Borrowing base report should exist for credit facility ${fundingRequestWithFullDetails.creditFacilityId}`,
  );
  const {
    cashAdvanceRate: _cashAdvanceRate,
    commitmentAmount: _commitmentAmount,
    currentPrincipalBalance: _currentPrincipalBalance,
    totalCash: _totalCash,
    borrowingBaseAmountAssets: _borrowingBaseAmountAssets,
    totalBorrowingBaseAmount: _totalBorrowingBaseAmount,
    reportingDate: _reportingDate,
    fileVersion: _fileVersion,
    maxReceivableAdvanceAmount: _maxReceivableAdvanceAmount,
    totalMaxAdvanceAmount: _totalMaxAdvanceAmount,
    cashReserveAmount: _cashReserveAmount,
    borrowingBaseReserveAmount: _borrowingBaseReserveAmount,
    borrowingBaseUtilization: _borrowingBaseUtilization,
  } = borrowingBaseReport;

  const _borrowingBaseUtilizationPercentage = _borrowingBaseUtilization * 100;

  // bankAccountMap is READONLY reference
  // NOTE: It's really better if graphql result already contains this map, but I'm not sure if it's possible
  const bankAccountMap = new Map(
    bankAccounts.map((v): [
      string,
      BankAccountWithInstitutionAndDesignationGQLType,
    ] => [v.id, v as BankAccountWithInstitutionAndDesignationGQLType]),
  );

  return {
    ...currentState,
    _status,
    bankAccountMap,
    _cashAdvanceRate,
    _commitmentAmount,
    _adjustedPrincipalBalance,
    _currentPrincipalBalance,
    _cashReserveAmount: _cashReserveAmount ?? 0,
    _borrowingBaseReserveAmount: _borrowingBaseReserveAmount ?? 0,
    _totalCash,
    _borrowingBaseAmountAssets,
    _totalBorrowingBaseAmount,
    _borrowingBaseUtilizationPercentage,
    _reportingDate,
    _fileVersion,
    _maxReceivableAdvanceAmount,
    _totalMaxAdvanceAmount,
    componentTotalAmounts: {
      curePaydownTotalAmount,
      collectionsPaydownTotalAmount,
      collectionsRecycleTotalAmount,
      drawRequestTotalAmount,
    },
    fundingRequestEvents: fundingRequestEvents || [],
    creditFacilityName: creditFacility.name || 'credit facility',
    fundingRequestReports: fundingRequestReports || [],
  };
};

const _componentToComponentChangeset = (
  component: FundingRequestComponentGQLType,
): UpdateFundingRequestComponentChangesetGQLType => {
  return {
    id: component.id,
    type: component.type,
    amount: component.amount,
    sourceBankAccountId: component.sourceBankAccountId,
    destinationBankAccountId: component.destinationBankAccountId,
    targetFundingDate: component.targetFundingDate,
  };
};

const _setComponentsFromServerData = (
  fundingRequestComponents: FundingRequestComponentGQLType[],
): FundingRequestComponentContainer => {
  const components: FundingRequestComponentContainer = {
    collectionsPaydown: [],
    collectionsRecycle: [],
    curePaydown: [],
    drawRequest: [],
  };

  _.forEach(fundingRequestComponents, (component) => {
    const componentChangeset = _componentToComponentChangeset(component);
    switch (component.type) {
      case 'curePaydown':
        components.curePaydown.push(componentChangeset);
        break;
      case 'collectionsPaydown':
        components.collectionsPaydown.push(componentChangeset);
        break;
      case 'collectionsRecycle':
        components.collectionsRecycle.push(componentChangeset);
        break;
      case 'drawRequest':
        components.drawRequest.push(componentChangeset);
        break;
      default:
        throw new Error(`Unsupported component type ${component.type}`);
    }
  });
  return components;
};

const _setComponentTotalAmountsFromServerData = (
  currentState: FundingRequestState,
) => {
  let updatedState = { ...currentState };
  const { componentTotalAmounts } = updatedState;

  updatedState = _updateCurePaydownChange(updatedState, {
    type: CURE_PAYDOWN_KEY,
    payload: { amount: componentTotalAmounts.curePaydownTotalAmount },
  });
  updatedState = _updateCollectionsPaydownChange(updatedState, {
    type: COLLECTIONS_PAYDOWN_KEY,
    payload: { amount: componentTotalAmounts.collectionsPaydownTotalAmount },
  });
  updatedState = _updateCollectionsRecycleChange(updatedState, {
    type: COLLECTIONS_RECYCLE_KEY,
    payload: { amount: componentTotalAmounts.collectionsRecycleTotalAmount },
  });
  updatedState = _updateDrawRequestChange(updatedState, {
    type: DRAW_REQUEST_KEY,
    payload: { amount: componentTotalAmounts.drawRequestTotalAmount },
  });

  return updatedState;
};

const _setFundingRequestWithFullDetailsAndConstraint = (
  currentState: FundingRequestState,
  serverData: GetFundingRequestWithFullDetailsAndConstraintQueryGQLType,
): FundingRequestState => {
  const {
    fundingRequestConstraint,
    fundingRequestWithFullDetails,
  } = serverData;

  const {
    _status,
    bankAccountMap,
    _cashAdvanceRate,
    _commitmentAmount,
    _adjustedPrincipalBalance,
    _currentPrincipalBalance,
    _borrowingBaseAmountAssets,
    _totalCash,
    _totalBorrowingBaseAmount,
    _borrowingBaseUtilizationPercentage,
    _reportingDate,
    _fileVersion,
    componentTotalAmounts,
    _maxReceivableAdvanceAmount,
    _totalMaxAdvanceAmount,
    _cashReserveAmount,
    _borrowingBaseReserveAmount,
  } = _refreshFundingRequestWithFullDetails(
    currentState,
    fundingRequestWithFullDetails,
  );

  const {
    creditFacility,
    fundingRequestComponents,
    fundingRequestEvents,
    fundingRequestReports,
  } = fundingRequestWithFullDetails;

  const constraints = _getConstraintsFromServerData({
    fundingRequestConstraint,
    _commitmentAmount,
    _adjustedPrincipalBalance,
    _currentPrincipalBalance,
    _cashAdvanceRate,
    _maxReceivableAdvanceAmount,
  });

  const components = _setComponentsFromServerData(fundingRequestComponents);

  const componentDefaultTargetFundingDates = _.reduce(
    fundingRequestComponents,
    (acc, component) => ({
      ...acc,
      [component.id]: component.targetFundingDate,
    }),
    {},
  );

  const {
    collectionsRecycleContributesToBorrowingBase,
    drawRequestContributesToBorrowingBase,
  } = constraints._masterConstraints;

  const borrowingBaseUtilizationPercentage = _calculateBorrowingBaseUtilizationPercentage(
    {
      _currentPrincipalBalance,
      _totalBorrowingBaseAmount,
      componentTotalAmounts,
      collectionsRecycleContributesToBorrowingBase,
      drawRequestContributesToBorrowingBase,
    },
  );

  const updatedState: FundingRequestState = {
    ...currentState,
    creditAgreementId: fundingRequestConstraint.creditAgreementId,
    creditFacilityId: fundingRequestWithFullDetails.creditFacilityId,
    _status,
    _reportingDate,
    _fileVersion,
    bankAccountMap,
    constraints,
    components,
    componentDefaultTargetFundingDates,
    componentTotalAmounts,
    borrowingBaseUtilizationPercentage,
    fundingRequestEvents: fundingRequestEvents || [],
    creditFacilityName: creditFacility.name || 'credit facility',
    fundingRequestReports: fundingRequestReports || [],
    _cashAdvanceRate,
    _commitmentAmount,
    _adjustedPrincipalBalance,
    _currentPrincipalBalance,
    _totalCash,
    _borrowingBaseAmountAssets,
    _totalBorrowingBaseAmount,
    _borrowingBaseUtilizationPercentage,
    _maxReceivableAdvanceAmount,
    _totalMaxAdvanceAmount,
    _cashReserveAmount,
    _borrowingBaseReserveAmount,
  };

  return _setComponentTotalAmountsFromServerData(updatedState);
};

const _allocateFundingRequestTotalAmounts = (
  currentState: FundingRequestState,
  fundingRequestAllocations: FundingRequestAllocationsGQLType,
): FundingRequestState => {
  const {
    fundingRequestComponentTotalAmounts: {
      drawRequestTotalAmount,
      collectionsPaydownTotalAmount,
      collectionsRecycleTotalAmount,
      curePaydownTotalAmount,
    },
    fundingRequestComponents,
  } = fundingRequestAllocations;

  const groupedComponents = _.reduce(
    fundingRequestComponents,
    (
      acc,
      {
        id,
        type,
        amount,
        sourceBankAccountId,
        destinationBankAccountId,
        targetFundingDate,
      },
    ) => {
      return {
        ...acc,
        [type]: [
          ...acc[type],
          {
            id,
            type,
            amount,
            sourceBankAccountId,
            destinationBankAccountId,
            targetFundingDate,
          },
        ],
      };
    },
    {
      collectionsPaydown: [],
      collectionsRecycle: [],
      curePaydown: [],
      drawRequest: [],
    } as FundingRequestComponentContainer,
  );

  return {
    ...currentState,
    componentTotalAmounts: {
      drawRequestTotalAmount,
      collectionsPaydownTotalAmount,
      collectionsRecycleTotalAmount,
      curePaydownTotalAmount,
    },
    components: groupedComponents,
  };
};

/**
 * helper method to update state on a target funding date change
 */
const _updateTargetFundingDate = (
  state: FundingRequestState,
  payload: {
    componentId: string;
    componentType: FundingRequestComponentTypeEnumGQLType;
    targetFundingDate: Util.FullDateStr;
  },
) => {
  const _state = _.cloneDeep(state);
  const _payload = _.cloneDeep(payload);
  const { componentId, componentType, targetFundingDate } = _payload;
  const { components: _components } = _state;

  // iterate through matching draw request components by type
  const updatedComponents = _.map(_components[componentType], (_component) => {
    const updatedComponent = _component;
    // update draw request component if ID matches
    if (updatedComponent.id === componentId) {
      updatedComponent.targetFundingDate = targetFundingDate;
    }
    return updatedComponent;
  });

  return {
    ..._state,
    components: {
      ...state.components,
      [componentType]: updatedComponents,
    },
  };
};

// ===== Reducer dispatch methods =====

export const updateCurePaydownAmount = (amount: number) => ({
  type: CURE_PAYDOWN_KEY,
  payload: { amount },
});

export const updateCollectionsPaydownAmount = (amount: number) => ({
  type: COLLECTIONS_PAYDOWN_KEY,
  payload: { amount },
});

export const updateCollectionsRecycleAmount = (amount: number) => ({
  type: COLLECTIONS_RECYCLE_KEY,
  payload: { amount },
});

export const updateDrawRequestAmount = (amount: number) => ({
  type: DRAW_REQUEST_KEY,
  payload: { amount },
});

export const setFundingRequestWithFullDetailsAndConstraint = (
  serverData: GetFundingRequestWithFullDetailsAndConstraintQueryGQLType,
) => ({
  type: SET_FUNDING_REQUEST_WITH_FULL_DETAILS_AND_CONSTRAINT,
  payload: serverData,
});

export const refreshFundingRequestWithFullDetails = (
  fundingRequestWithFullDetails: FundingRequestWithFullDetailsFragmentGQLType,
) => ({
  type: REFRESH_FUNDING_REQUEST_WITH_FULL_DETAILS,
  payload: fundingRequestWithFullDetails,
});

export const allocateFundingRequestTotalAmounts = (
  fundingRequestAllocations: FundingRequestAllocationsGQLType,
) => ({
  type: ALLOCATE_FUNDING_REQUEST_TOTAL_AMOUNTS,
  payload: fundingRequestAllocations,
});

export const updateTargetFundingDate = (
  componentId: string,
  componentType: FundingRequestComponentTypeEnumGQLType,
  targetFundingDate: Util.FullDateStr,
) => ({
  type: UPDATE_TARGET_FUNDING_DATE,
  payload: {
    componentId,
    componentType,
    targetFundingDate,
  },
});

// We expose this reducer function, but it should only be used in Context.tsx file.
// 'dispatch' reference should be accessed from useFundingRequestContext() hook and use
// one of the dispatch methods, which has more strict type enforcement
export const fundingRequestReducer = (
  state: FundingRequestState,
  action: { type: string; payload: any },
) => {
  switch (action.type) {
    case CURE_PAYDOWN_KEY:
      return _updateCurePaydownChange(state, action);
    case COLLECTIONS_PAYDOWN_KEY:
      return _updateCollectionsPaydownChange(state, action);
    case COLLECTIONS_RECYCLE_KEY:
      return _updateCollectionsRecycleChange(state, action);
    case DRAW_REQUEST_KEY:
      return _updateDrawRequestChange(state, action);
    case UPDATE_CONTROL_METRICS:
      return {
        ...state,
        ...action.payload,
      };
    case SET_FUNDING_REQUEST_WITH_FULL_DETAILS_AND_CONSTRAINT:
      return _setFundingRequestWithFullDetailsAndConstraint(
        state,
        action.payload,
      );
    case REFRESH_FUNDING_REQUEST_WITH_FULL_DETAILS:
      return _refreshFundingRequestWithFullDetails(state, action.payload);
    case ALLOCATE_FUNDING_REQUEST_TOTAL_AMOUNTS:
      return _allocateFundingRequestTotalAmounts(state, action.payload);
    case UPDATE_TARGET_FUNDING_DATE:
      return _updateTargetFundingDate(state, action.payload);
    default:
      throw Error('Unknown fundingRequestReducer action type');
  }
};
